// An observation, with the parts Oreia can prove marked as tappable.
//
// The underline is deliberately quiet — a footnote, not a hyperlink. Someone reading what Oreia
// noticed about them should read a sentence, not a page of links; the marks are there for the
// moment they stop and ask "wait, where did you get that?".
//
// Tapping opens the source entry with the quoted passage selected. The quote travels in router
// state rather than the URL: it is the person's own journal text, and it has no business sitting
// in a browser history entry (the same reason HomeScreen hands drafts over in state).

import { useNavigate } from 'react-router-dom';
import { citedSegments, type Citation } from '../api/citations';

export interface CitedObservationProps {
  text: string;
  citations: Citation[];
  className?: string;
  style?: React.CSSProperties;
  /** Rendered element. Observation cards use an h3; inline contexts may want a p. */
  as?: 'h3' | 'p' | 'div';
}

export function CitedObservation({ text, citations, className, style, as = 'h3' }: CitedObservationProps) {
  const navigate = useNavigate();
  const segments = citedSegments(text, citations);
  const Tag = as;

  const open = (citation: Citation) => {
    navigate(`/write/${encodeURIComponent(citation.entryId)}`, {
      state: { highlight: { quote: citation.quote, start: citation.start, end: citation.end } },
    });
  };

  // Nothing verifiable to mark — render the sentence plainly rather than inventing emphasis.
  if (segments.every((s) => !s.citation)) {
    return <Tag className={className} style={style}>{text}</Tag>;
  }

  return (
    <Tag className={className} style={style}>
      {segments.map((segment, i) =>
        segment.citation ? (
          <button
            key={`${segment.citation.id}-${i}`}
            type="button"
            className="ocite"
            onClick={() => open(segment.citation!)}
            title="See where this came from"
          >
            {segment.text}
          </button>
        ) : (
          <span key={`plain-${i}`}>{segment.text}</span>
        ),
      )}
    </Tag>
  );
}
